import React, { useEffect, useState, useCallback } from 'react';
import { invoiceAPI, profileAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  DRAFT: { bg: '#f0f0f0', color: '#666' },
  SENT:  { bg: '#e8f4fd', color: '#1565c0' },
  PAID:  { bg: '#e8f5e9', color: '#2e7d32' },
};

const GST_RATES = [0, 5, 12, 18, 28];

const emptyForm = {
  brandName: '', brandGstin: '', brandAddress: '', brandEmail: '',
  serviceDescription: '', amount: '', gstRate: 18, isInterState: false,
  invoiceDate: new Date().toISOString().slice(0, 10), dueDate: '',
  status: 'DRAFT', notes: '',
  creatorName: '', creatorGstin: '', creatorAddress: '', creatorEmail: '', creatorPhone: '',
};

export default function InvoicesPage() {
  const { user, setUser } = useAuth();
  const [invoices, setInvoices]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [editId, setEditId]           = useState(null);
  const [form, setForm]               = useState(emptyForm);
  const [profileForm, setProfileForm] = useState({
    name: '', gstin: '', businessName: '', address: '', phone: '',
  });
  const [saving, setSaving]           = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError]             = useState('');

  const load = useCallback(async () => {
    try {
      const res = await invoiceAPI.getAll();
      setInvoices(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pre-fill creator fields from user profile
  useEffect(() => {
    if (user) {
      setProfileForm({
        name:         user.name         || '',
        gstin:        user.gstin        || '',
        businessName: user.businessName || '',
        address:      user.address      || '',
        phone:        user.phone        || '',
      });
    }
  }, [user]);

  // ── Stats ────────────────────────────────────────────────────
  const totalEarned = invoices.filter(i => i.status === 'PAID')
    .reduce((s, i) => s + parseFloat(i.totalAmount || 0), 0);
  const totalPending = invoices.filter(i => i.status !== 'PAID')
    .reduce((s, i) => s + parseFloat(i.totalAmount || 0), 0);

  // ── Computed GST preview ─────────────────────────────────────
  const amt   = parseFloat(form.amount) || 0;
  const rate  = parseFloat(form.gstRate) || 0;
  const tax   = parseFloat(((amt * rate) / 100).toFixed(2));
  const total = parseFloat((amt + tax).toFixed(2));
  const half  = parseFloat((tax / 2).toFixed(2));

  // ── Open new invoice form ────────────────────────────────────
  const openNew = () => {
    setEditId(null);
    setForm({
      ...emptyForm,
      creatorName:    user?.businessName || user?.name || '',
      creatorGstin:   user?.gstin        || '',
      creatorAddress: user?.address      || '',
      creatorEmail:   user?.email        || '',
      creatorPhone:   user?.phone        || '',
    });
    setError('');
    setShowForm(true);
  };

  const openEdit = (inv) => {
    setEditId(inv.id);
    setForm({
      brandName:          inv.brandName          || '',
      brandGstin:         inv.brandGstin         || '',
      brandAddress:       inv.brandAddress       || '',
      brandEmail:         inv.brandEmail         || '',
      serviceDescription: inv.serviceDescription || '',
      amount:             inv.amount             || '',
      gstRate:            inv.gstRate            || 18,
      isInterState:       inv.isInterState       || false,
      invoiceDate:        inv.invoiceDate        || '',
      dueDate:            inv.dueDate            || '',
      status:             inv.status             || 'DRAFT',
      notes:              inv.notes              || '',
      creatorName:        inv.creatorName        || '',
      creatorGstin:       inv.creatorGstin       || '',
      creatorAddress:     inv.creatorAddress     || '',
      creatorEmail:       inv.creatorEmail       || '',
      creatorPhone:       inv.creatorPhone       || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.brandName.trim()) { setError('Brand name is required'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Amount must be greater than 0'); return; }

    setSaving(true);
    setError('');
    try {
      if (editId) {
        await invoiceAPI.update(editId, form);
      } else {
        await invoiceAPI.create(form);
      }
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save invoice');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    await invoiceAPI.delete(id);
    load();
  };

  const handleStatusChange = async (id, status) => {
    await invoiceAPI.updateStatus(id, status);
    load();
  };

  const handleDownloadPdf = async (inv) => {
    setDownloadingId(inv.id);
    try {
      const res = await invoiceAPI.downloadPdf(inv.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href    = url;
      a.download = `${inv.invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to download PDF'); }
    setDownloadingId(null);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await profileAPI.update(profileForm);
      if (setUser) setUser(prev => ({ ...prev, ...res.data }));
      setShowProfile(false);
    } catch { alert('Failed to update profile'); }
    setSaving(false);
  };

  const f = (v) => setForm(p => ({ ...p, ...v }));

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading invoices...</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Header ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e' }}>GST Invoices 🧾</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>
            Generate professional GST-compliant invoices for brand deals
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => setShowProfile(true)} style={outlineBtn}>
            ⚙️ My Profile
          </button>
          <button onClick={openNew} style={primaryBtn}>
            + New Invoice
          </button>
        </div>
      </div>

      {/* ── Stats ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Invoices', value: invoices.length, icon: '🧾' },
          { label: 'Amount Paid',    value: `₹${totalEarned.toLocaleString('en-IN')}`, icon: '✅' },
          { label: 'Pending',        value: `₹${totalPending.toLocaleString('en-IN')}`, icon: '⏳' },
          { label: 'Draft',          value: invoices.filter(i => i.status === 'DRAFT').length, icon: '📝' },
        ].map(s => (
          <div key={s.label} style={statCard}>
            <div style={{ fontSize: '1.4rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1a1a2e' }}>{s.value}</div>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── No GSTIN warning ──────────────────────────────────── */}
      {!user?.gstin && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffcc02', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#7a5c00', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          ⚠️ Add your GSTIN in <button onClick={() => setShowProfile(true)} style={{ background: 'none', border: 'none', color: '#1a7a4a', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}>My Profile</button> so invoices auto-fill your details.
        </div>
      )}

      {/* ── Invoice List ──────────────────────────────────────── */}
      {invoices.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: '3rem' }}>🧾</div>
          <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1a2e' }}>No invoices yet</div>
          <div style={{ color: '#888', fontSize: '0.875rem', marginTop: '0.25rem' }}>Create your first GST invoice for a brand deal</div>
          <button onClick={openNew} style={{ ...primaryBtn, marginTop: '1rem' }}>+ Create Invoice</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {invoices.map(inv => (
            <div key={inv.id} style={invoiceCard}>
              {/* Left */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, color: '#1a1a2e', fontSize: '0.95rem' }}>{inv.brandName}</span>
                  <span style={{ fontSize: '0.75rem', color: '#888' }}>{inv.invoiceNumber}</span>
                  <span style={{
                    ...badge,
                    background: STATUS_COLORS[inv.status]?.bg || '#f0f0f0',
                    color: STATUS_COLORS[inv.status]?.color || '#666',
                  }}>{inv.status}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.3rem' }}>
                  {inv.serviceDescription
                    ? inv.serviceDescription.slice(0, 80) + (inv.serviceDescription.length > 80 ? '…' : '')
                    : 'Content Creation Services'}
                  {' · '}
                  <span>{inv.invoiceDate}</span>
                  {inv.dueDate && <span> · Due: {inv.dueDate}</span>}
                </div>
              </div>
              {/* Right */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, color: '#1a1a2e', fontSize: '1rem' }}>
                    ₹{parseFloat(inv.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#888' }}>
                    Base ₹{parseFloat(inv.amount || 0).toLocaleString('en-IN')} + GST {inv.gstRate}%
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  {/* Status changer */}
                  <select value={inv.status}
                    onChange={e => handleStatusChange(inv.id, e.target.value)}
                    style={selectSmall}>
                    <option value="DRAFT">Draft</option>
                    <option value="SENT">Sent</option>
                    <option value="PAID">Paid</option>
                  </select>
                  <button onClick={() => handleDownloadPdf(inv)} style={iconBtn} title="Download PDF"
                    disabled={downloadingId === inv.id}>
                    {downloadingId === inv.id ? '⏳' : '⬇️'}
                  </button>
                  <button onClick={() => openEdit(inv)} style={iconBtn} title="Edit">✏️</button>
                  <button onClick={() => handleDelete(inv.id)} style={{ ...iconBtn, color: '#e53e3e' }} title="Delete">🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create/Edit Invoice Modal ─────────────────────────── */}
      {showForm && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#1a1a2e' }}>
                {editId ? 'Edit Invoice' : 'New GST Invoice'}
              </h2>
              <button onClick={() => setShowForm(false)} style={closeBtn}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem' }}>

              {/* Section: Your Details */}
              <div style={{ ...sectionHeader, gridColumn: '1/-1' }}>📤 Your Details</div>

              <label style={labelStyle}>
                Your Name / Business Name *
                <input value={form.creatorName} onChange={e => f({ creatorName: e.target.value })} style={input} placeholder="e.g. Raju Tambe" />
              </label>
              <label style={labelStyle}>
                Your GSTIN
                <input value={form.creatorGstin} onChange={e => f({ creatorGstin: e.target.value.toUpperCase() })} style={input} placeholder="27AAAAA0000A1Z5" maxLength={15} />
              </label>
              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
                Your Address
                <textarea value={form.creatorAddress} onChange={e => f({ creatorAddress: e.target.value })} style={{ ...input, height: 60, resize: 'vertical' }} placeholder="Street, City, State, PIN" />
              </label>
              <label style={labelStyle}>
                Your Email
                <input value={form.creatorEmail} onChange={e => f({ creatorEmail: e.target.value })} style={input} type="email" />
              </label>
              <label style={labelStyle}>
                Your Phone
                <input value={form.creatorPhone} onChange={e => f({ creatorPhone: e.target.value })} style={input} placeholder="+91 9876543210" />
              </label>

              {/* Section: Brand Details */}
              <div style={{ ...sectionHeader, gridColumn: '1/-1' }}>🏢 Brand / Client Details</div>

              <label style={labelStyle}>
                Brand Name *
                <input value={form.brandName} onChange={e => f({ brandName: e.target.value })} style={input} placeholder="e.g. Noise Gadgets" />
              </label>
              <label style={labelStyle}>
                Brand GSTIN
                <input value={form.brandGstin} onChange={e => f({ brandGstin: e.target.value.toUpperCase() })} style={input} placeholder="27BBBBB1111B2Z6" maxLength={15} />
              </label>
              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
                Brand Address
                <textarea value={form.brandAddress} onChange={e => f({ brandAddress: e.target.value })} style={{ ...input, height: 60, resize: 'vertical' }} placeholder="Brand's registered address" />
              </label>
              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
                Brand Email
                <input value={form.brandEmail} onChange={e => f({ brandEmail: e.target.value })} style={input} type="email" placeholder="finance@brand.com" />
              </label>

              {/* Section: Invoice Details */}
              <div style={{ ...sectionHeader, gridColumn: '1/-1' }}>💰 Invoice Details</div>

              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
                Service Description
                <textarea value={form.serviceDescription} onChange={e => f({ serviceDescription: e.target.value })} style={{ ...input, height: 70, resize: 'vertical' }} placeholder="e.g. Sponsored YouTube video + 2 Instagram reels for Product X campaign (Oct 2024)" />
              </label>
              <label style={labelStyle}>
                Base Amount (₹) *
                <input value={form.amount} onChange={e => f({ amount: e.target.value })} style={input} type="number" min="0" placeholder="50000" />
              </label>
              <label style={labelStyle}>
                GST Rate
                <select value={form.gstRate} onChange={e => f({ gstRate: e.target.value })} style={input}>
                  {GST_RATES.map(r => <option key={r} value={r}>{r}% {r === 18 ? '(Standard)' : r === 0 ? '(Exempt)' : ''}</option>)}
                </select>
              </label>

              {/* GST Preview */}
              {amt > 0 && (
                <div style={{ gridColumn: '1/-1', background: '#f0f9f4', border: '1px solid #c8ebd8', borderRadius: 10, padding: '0.9rem', fontSize: '0.85rem' }}>
                  <div style={{ fontWeight: 700, color: '#1a7a4a', marginBottom: '0.5rem' }}>Tax Breakdown Preview</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', color: '#333' }}>
                    <div><span style={{ color: '#888' }}>Base Amount</span><br /><b>₹{amt.toLocaleString('en-IN')}</b></div>
                    {form.isInterState
                      ? <div><span style={{ color: '#888' }}>IGST ({rate}%)</span><br /><b>₹{tax.toLocaleString('en-IN')}</b></div>
                      : <>
                          <div><span style={{ color: '#888' }}>CGST ({rate / 2}%)</span><br /><b>₹{half.toLocaleString('en-IN')}</b></div>
                          <div><span style={{ color: '#888' }}>SGST ({rate / 2}%)</span><br /><b>₹{half.toLocaleString('en-IN')}</b></div>
                        </>
                    }
                    <div style={{ gridColumn: form.isInterState ? '3' : '1/-1' }}>
                      <span style={{ color: '#888' }}>Total Payable</span><br />
                      <b style={{ color: '#1a7a4a', fontSize: '1.05rem' }}>₹{total.toLocaleString('en-IN')}</b>
                    </div>
                  </div>
                </div>
              )}

              <label style={labelStyle}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input type="checkbox" checked={form.isInterState} onChange={e => f({ isInterState: e.target.checked })} />
                  Inter-State Supply (IGST)
                </span>
                <span style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.2rem', display: 'block' }}>
                  Check if brand is in a different state → IGST; otherwise → CGST+SGST
                </span>
              </label>

              <label style={labelStyle}>
                Invoice Date *
                <input value={form.invoiceDate} onChange={e => f({ invoiceDate: e.target.value })} style={input} type="date" />
              </label>
              <label style={labelStyle}>
                Due Date
                <input value={form.dueDate} onChange={e => f({ dueDate: e.target.value })} style={input} type="date" />
              </label>
              <label style={labelStyle}>
                Status
                <select value={form.status} onChange={e => f({ status: e.target.value })} style={input}>
                  <option value="DRAFT">Draft</option>
                  <option value="SENT">Sent</option>
                  <option value="PAID">Paid</option>
                </select>
              </label>
              <label style={{ ...labelStyle, gridColumn: '1/-1' }}>
                Notes (optional)
                <textarea value={form.notes} onChange={e => f({ notes: e.target.value })} style={{ ...input, height: 60, resize: 'vertical' }} placeholder="Payment terms, bank details, etc." />
              </label>
            </div>

            {error && <div style={{ color: '#e53e3e', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={outlineBtn}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={primaryBtn}>
                {saving ? 'Saving…' : editId ? 'Update Invoice' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Profile Modal ─────────────────────────────────────── */}
      {showProfile && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 500 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#1a1a2e' }}>⚙️ Business Profile</h2>
              <button onClick={() => setShowProfile(false)} style={closeBtn}>✕</button>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#888', margin: '0 0 1rem' }}>
              This info auto-fills on every invoice you create.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              {[
                { key: 'name',         label: 'Your Name',           placeholder: 'Full name' },
                { key: 'businessName', label: 'Business / Brand Name', placeholder: 'e.g. Raju Creations' },
                { key: 'gstin',        label: 'GSTIN',               placeholder: '27AAAAA0000A1Z5' },
                { key: 'phone',        label: 'Phone',               placeholder: '+91 9876543210' },
              ].map(({ key, label, placeholder }) => (
                <label key={key} style={labelStyle}>
                  {label}
                  <input
                    value={profileForm[key]}
                    onChange={e => setProfileForm(p => ({ ...p, [key]: key === 'gstin' ? e.target.value.toUpperCase() : e.target.value }))}
                    style={input} placeholder={placeholder}
                  />
                </label>
              ))}
              <label style={labelStyle}>
                Address
                <textarea value={profileForm.address} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))}
                  style={{ ...input, height: 70, resize: 'vertical' }} placeholder="Street, City, State, PIN" />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowProfile(false)} style={outlineBtn}>Cancel</button>
              <button onClick={handleSaveProfile} disabled={saving} style={primaryBtn}>
                {saving ? 'Saving…' : 'Save Profile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const primaryBtn = {
  background: '#1a7a4a', color: 'white', border: 'none', borderRadius: 10,
  padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer',
};
const outlineBtn = {
  background: 'white', color: '#1a7a4a', border: '1.5px solid #1a7a4a', borderRadius: 10,
  padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer',
};
const statCard = {
  background: 'white', borderRadius: 14, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  display: 'flex', flexDirection: 'column', gap: '0.2rem',
};
const invoiceCard = {
  background: 'white', borderRadius: 14, padding: '1rem 1.25rem',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
};
const badge = {
  padding: '0.18rem 0.6rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
};
const iconBtn = {
  background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.55rem',
  cursor: 'pointer', fontSize: '0.9rem',
};
const selectSmall = {
  padding: '0.35rem 0.5rem', borderRadius: 8, border: '1px solid #e0e0e0',
  fontSize: '0.78rem', cursor: 'pointer', background: 'white',
};
const emptyState = {
  textAlign: 'center', padding: '4rem 2rem', background: 'white',
  borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: '1rem',
};
const modal = {
  background: 'white', borderRadius: 18, padding: '1.75rem',
  width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
};
const closeBtn = {
  background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.7rem',
  cursor: 'pointer', fontSize: '1rem',
};
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: '#444' };
const input = {
  padding: '0.55rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8,
  fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none',
  transition: 'border-color 0.15s', background: 'white',
};
const sectionHeader = {
  fontWeight: 800, fontSize: '0.8rem', color: '#1a7a4a',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  paddingBottom: '0.2rem', borderBottom: '1px solid #e8f5e9',
};
